// frontend/src/modules/integrations/api.ts
//
// Thin client over /api/integrations/quickbooks.

import { api } from '../../core/api-client';
import type {
  QuickBooksStatus, QuickBooksTestResult, QuickBooksPushResult,
  QuickBooksExpensePushResult, QuickBooksPayrollPushResult, QuickBooksPaymentPushResult,
  QuickBooksPnl, QuickBooksCashFlow,
  QuickBooksEntityType, QuickBooksSyncResponse, QuickBooksPushOptions,
} from './types';

/**
 * Pushes are idempotent server-side: without `force` a second push returns the
 * QBO id already on record instead of creating a duplicate. Only send a body
 * when forcing, so the common case stays a plain POST.
 */
function pushBody(opts?: QuickBooksPushOptions) {
  return opts?.force
    ? { method: 'POST' as const, body: { force: true } }
    : { method: 'POST' as const };
}

export const quickbooksApi = {
  connect: () => api<{ url: string }>('/api/integrations/quickbooks/connect'),
  status: () => api<QuickBooksStatus>('/api/integrations/quickbooks/status'),
  disconnect: () => api<{ ok: boolean }>('/api/integrations/quickbooks/disconnect', { method: 'POST' }),
  test: () => api<QuickBooksTestResult>('/api/integrations/quickbooks/test', { method: 'POST' }),
  syncStatus: (type: QuickBooksEntityType, ids: string[]) =>
    api<QuickBooksSyncResponse>(
      `/api/integrations/quickbooks/sync?type=${type}&ids=${encodeURIComponent(ids.join(','))}`,
    ),
  pushInvoice: (invoiceId: string, opts?: QuickBooksPushOptions) =>
    api<QuickBooksPushResult>(`/api/integrations/quickbooks/invoices/${invoiceId}/push`, pushBody(opts)),
  pushExpense: (expenseId: string, opts?: QuickBooksPushOptions) =>
    api<QuickBooksExpensePushResult>(`/api/integrations/quickbooks/expenses/${expenseId}/push`, pushBody(opts)),
  pushPayrollRun: (runId: string, opts?: QuickBooksPushOptions) =>
    api<QuickBooksPayrollPushResult>(`/api/integrations/quickbooks/payroll/runs/${runId}/push`, pushBody(opts)),
  pushPayment: (paymentId: string, opts?: QuickBooksPushOptions) =>
    api<QuickBooksPaymentPushResult>(`/api/integrations/quickbooks/payments/${paymentId}/push`, pushBody(opts)),
  profitAndLoss: (start: string, end: string) =>
    api<{ report: QuickBooksPnl }>(`/api/integrations/quickbooks/reports/profit-and-loss?start_date=${start}&end_date=${end}`),
  cashFlow: (start: string, end: string) =>
    api<{ report: QuickBooksCashFlow }>(`/api/integrations/quickbooks/reports/cash-flow?start_date=${start}&end_date=${end}`),
};
